/**
 * Acceso a localStorage/sessionStorage que NUNCA lanza.
 *
 * WebKit (Safari/iOS, y la web dentro de la app) puede dejar
 * `window.localStorage` en `null` cuando la pestaña pasa mucho tiempo
 * suspendida, en modo privado o cuando ITP desaloja el storage. En ese estado
 * cualquier `localStorage.getItem` revienta con "null is not an object"
 * (visto en Sentry sobre AuthContext): se caían los handlers de tabs, un login
 * exitoso parecía fallar (el setItem posterior lanzaba) y los reintentos
 * acababan en el rate limiter.
 *
 * Este wrapper cae a un Map en memoria: la sesión sigue funcionando dentro de
 * la pestaña aunque el storage real no esté (se pierde al cerrarla, que es
 * exactamente lo que el navegador ya decidió). También cubre `typeof
 * localStorage !== 'undefined'`, que NO protege del caso null.
 *
 * Regla del repo: NINGÚN código nuevo toca localStorage/sessionStorage
 * directo — siempre safeStorage/safeSession.
 */

type WebStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function makeSafe(pick: () => Storage | null): WebStorage {
  const memory = new Map<string, string>();
  return {
    getItem(key: string): string | null {
      try {
        const real = pick();
        if (real) {
          const v = real.getItem(key);
          if (v !== null) return v;
        }
      } catch { /* storage revocado: cae a memoria */ }
      return memory.get(key) ?? null;
    },
    setItem(key: string, value: string): void {
      memory.set(key, value);
      try { pick()?.setItem(key, value); } catch { /* solo memoria */ }
    },
    removeItem(key: string): void {
      memory.delete(key);
      try { pick()?.removeItem(key); } catch { /* solo memoria */ }
    },
  };
}

export const safeStorage: WebStorage = makeSafe(() =>
  typeof window !== 'undefined' ? window.localStorage : null
);

export const safeSession: WebStorage = makeSafe(() =>
  typeof window !== 'undefined' ? window.sessionStorage : null
);
